import type { TFunction } from 'i18next';

import type { APIKeyServiceNames } from '@feathr/blackbox';
import { APIKey } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';

/** Expiration presets for a service key. */
export enum EExpirationPreset {
  SevenDays = '7',
  ThirtyDays = '30',
  SixtyDays = '60',
  NinetyDays = '90',
  Custom = 'custom',
  NoExpiration = 'none',
}

export interface IExpirationOption extends ISelectOption {
  id: EExpirationPreset;
  name: string;
}

/** Number of days each numeric preset adds to the reference date. */
const PRESET_DAYS: Partial<Record<EExpirationPreset, number>> = {
  [EExpirationPreset.SevenDays]: 7,
  [EExpirationPreset.ThirtyDays]: 30,
  [EExpirationPreset.SixtyDays]: 60,
  [EExpirationPreset.NinetyDays]: 90,
};

/** ISO 8601 `expires_at` for a numeric preset; `undefined` for Custom/NoExpiration. */
export function getExpiresAt(preset: EExpirationPreset, reference: Date): string | undefined {
  const days = PRESET_DAYS[preset];
  if (days === undefined) {
    return undefined;
  }
  const expires = new Date(reference.getTime());
  expires.setUTCDate(expires.getUTCDate() + days);
  return expires.toISOString();
}

/** ISO 8601 `expires_at` for a picked date, normalized to UTC midnight. */
export function getExpiresAtForDate(date: Date): string {
  const expires = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  return expires.toISOString();
}

/** Human-readable `YYYY-MM-DD` slice of an ISO 8601 datetime for inline labels. */
function formatExpiryDate(isoDatetime: string): string {
  return isoDatetime.slice(0, 10);
}

/** Analytics shape for the chosen expiry: whether one was set and, if so, its whole-day span. */
export interface IExpirationAnalytics {
  has_expiration: boolean;
  expiration_days: number | null;
}

/** PostHog `api_token_created` expiry props: undefined => {false, null}; a date => {true, whole days}. */
export function getExpirationAnalytics(
  expiresAt: string | undefined,
  reference: Date,
): IExpirationAnalytics {
  if (expiresAt === undefined) {
    return { has_expiration: false, expiration_days: null };
  }
  // Count whole days UTC-midnight to midnight, so the reference's time-of-day
  // doesn't skew a midnight-normalized custom expiry.
  const utcDay = (date: Date): number =>
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const deltaMs = utcDay(new Date(expiresAt)) - utcDay(reference);
  return { has_expiration: true, expiration_days: Math.round(deltaMs / msPerDay) };
}

/** Select options: numeric presets with the computed date inline, plus Custom and No expiration. */
export function getExpirationOptions(t: TFunction, reference: Date): IExpirationOption[] {
  const numeric: EExpirationPreset[] = [
    EExpirationPreset.SevenDays,
    EExpirationPreset.ThirtyDays,
    EExpirationPreset.SixtyDays,
    EExpirationPreset.NinetyDays,
  ];

  const options: IExpirationOption[] = numeric.map((preset) => {
    const expiresAt = getExpiresAt(preset, reference)!;
    return {
      id: preset,
      name: t('{{days}} days ({{date}})', {
        date: formatExpiryDate(expiresAt),
        days: PRESET_DAYS[preset],
      }),
    };
  });

  options.push({ id: EExpirationPreset.Custom, name: t('Custom date') });
  options.push({ id: EExpirationPreset.NoExpiration, name: t('No expiration') });

  return options;
}

/**
 * Validation messages for a prospective key name, from the APIKey model's
 * `service_name` constraints. Validates the trimmed name, since that is what
 * handleCreate submits.
 */
export function validateAPIKeyName(name: string): string[] {
  const draft = new APIKey({ service_name: name.trim() as APIKeyServiceNames });
  return [...draft.validate(['service_name'], false).errors];
}
